import React, { useCallback, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Column, Row } from "react-table";
import ImagerApi from "../../api/ImagerApi";
import SchedulingApi from "../../api/SchedulingApi";
import IconImageNow from "../../Icons/Command/ImageNow";
import IconMovePlateTo from "../../Icons/Command/MovePlateTo";
import IconPlateInfo from "../../Icons/Command/PlateInfo";
import IconCancelImaging from "../../Icons/TopCommandPanel/CancelImaging";
import IconChangePriority from "../../Icons/TopCommandPanel/ChangePriority";
import IconClearDropLocation from "../../Icons/TopCommandPanel/ClearAllDrop";
import IconMoveToFutureImaging from "../../Icons/TopCommandPanel/MoveToFutureImaging";
import IconMoveToWaitingToBeImaged from "../../Icons/TopCommandPanel/MoveToWaitingToBeImaged";
import IconStartAll from "../../Icons/TopCommandPanel/StartAll";
import IconStartImaging from "../../Icons/TopCommandPanel/StartImaging";
import IconStopAll from "../../Icons/TopCommandPanel/StopAll";
import IconStopImaging from "../../Icons/TopCommandPanel/StopImaging";
import { EDropLocationCacheStatus } from "../../Model/EDropLocationCacheStatus";
import { EScheduleState, ScheduleEntry } from "../../Model/ImagingSchedule";
import { EPlateErrorStatus, CanMovePlate } from "../../Model/Plate";
import { PlateAddress } from "../../Model/PlateAddress";
import {
  getQueuedPlates,
  getScheduledPlates,
} from "../../redux/reducers/ImagingScheduleReducer";
import { getModuleState } from "../../redux/reducers/ModuleStateReducer";
import { getPlateAddresses } from "../../redux/reducers/PlateAddressReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import DateUtil from "../../Utility/DateUtil";
import CommandPanel from "../CommandPanel/CommandPanel";
import CommonTable from "../Common/CommonTable";
import HeaderButton from "../Header/HeaderButton";
import LayoutContent from "../LayoutContent";
import ClearDropLocation from "../Plate/CommandPanelViews/ClearDropLocation";
import MovePlate from "../Plate/CommandPanelViews/MovePlate";
import PlateInformation from "../Plate/CommandPanelViews/PlateInformation";
import CommandButton from "./../CommandPanel/CommandPanelButton";
import Breadcrumb from "./../Header/Breadcrumb";
import Header from "./../Header/Header";
import ImagePlateUsing from "./../Plate/CommandPanelViews/ImagePlateUsing";
import ChangePriority from "./ChangePriority";
import SchedulingNavSubMenu from "./SchedulingNavSubMenu";
import { EImagingMode } from "../../Model/EImagingMode";
import PlateApi from "../../api/PlateApi";
import PlateUtil from "../../Utility/PlateUtil";
import { EModuleType } from "../../Model/EModuleType";
import { getModulesByType } from "../../redux/reducers/ModuleReducer";

const MOVE = "MOVE";
const IMAGEPLATE = "IMAGE";
const INFO = "INFO";
const SCHEDULING = "SCHEDULING";
const CANCEL_IMAGING = "CANCEL_IMAGING";
const PAUSE_IMAGING = "PAUSE_IMAGING";
const START_IMAGING = "START_IMAGING";
const CHANGE_PRIORITY = "CHANGE_PRIORITY";
const CLEAR_DROP = "CLEAR_DROP";

const scheduleStateSort: { [index: number]: number } = {
  [EScheduleState.Completed]: 0,
  [EScheduleState.Imaging]: 1,
  [EScheduleState.Pending]: 2,
  [EScheduleState.Queued]: 3,
  [EScheduleState.Paused]: 4,
  [EScheduleState.Cancelled]: 5,
  [EScheduleState.PlateMovementError]: 6,
  [EScheduleState.Skipped]: 7,
  [EScheduleState.Unknown]: 8,
};

const sortByFn = (
  rowA: Row<ScheduleEntry>,
  rowB: Row<ScheduleEntry>,
  columnId: string,
  desc?: boolean
) => {
  if (columnId !== "status") {
    return 0;
  }

  const a = scheduleStateSort[rowA.original.scheduleState];
  const b = scheduleStateSort[rowB.original.scheduleState];

  if (a === b) {
    return 0;
  } else if (a > b) {
    return -1;
  } else {
    return 1;
  }
};

function getTableColumns(isFuturePlates: boolean): Column<ScheduleEntry>[] {
  // let lightPaths: Column<ScheduleEntry> = {
  //   Header: "LightPaths",
  //   id: "lightpaths",
  //   accessor: (row) => {
  //     return row.lightPaths;
  //   },
  // };

  const project: Column<ScheduleEntry> = {
    Header: "Project",
    id: "project",
    accessor: (row) => {
      return row.projectName;
    },
  };

  const experiment: Column<ScheduleEntry> = {
    Header: "Experiment",
    id: "experiment",
    accessor: (row) => {
      return row.experimentName;
    },
  };

  const plateNumber: Column<ScheduleEntry> = {
    Header: "Plate Number",
    id: "plateNumber",
    accessor: (row) => {
      return row.plateNumber;
    },
    width: "7%",
  };

  const dateToImage: Column<ScheduleEntry> = {
    Header: "Date To Image",
    id: "dateToImage",
    accessor: (row) => {
      const date = new Date(row.dateToImage);
      return DateUtil.toUsFormatString(date);
    },
  };

  const userName: Column<ScheduleEntry> = {
    Header: "User Name",
    id: "userName",
    accessor: (row) => {
      return row.username;
    },
  };

  const plateType: Column<ScheduleEntry> = {
    Header: "Plate Type",
    id: "plateType",
    accessor: (row) => {
      return row.plateTypeName;
    },
  };

  const plateId: Column<ScheduleEntry> = {
    Header: "Plate ID",
    id: "plateId",
    accessor: (row) => {
      return row.plateId;
    },
    width: "7%",
  };

  const priority: Column<ScheduleEntry> = {
    Header: "Priority",
    id: "priority",
    accessor: (row) => {
      return row.priority;
    },
    width: "7%",
  };

  const state: Column<ScheduleEntry> = {
    Header: "Status",
    id: "status",
    accessor: (row) => {
      return getStatus(row);
    },
    sortType: sortByFn,
    width: "7%",
  };

  return isFuturePlates
    ? [
        dateToImage,
        project,
        experiment,
        plateNumber,
        plateType,
        plateId,
        userName,
        priority,
      ]
    : [
        dateToImage,
        project,
        experiment,
        plateNumber,
        plateType,
        plateId,
        userName,
        priority,
        state,
      ];
}

function getStatus(scheduleEntry: ScheduleEntry): string {
  switch (scheduleEntry.scheduleState) {
    case EScheduleState.Queued: {
      return "Queued";
    }

    case EScheduleState.Pending: {
      return "Pending";
    }

    case EScheduleState.Imaging: {
      return "Imaging";
    }

    case EScheduleState.Paused: {
      return "Paused";
    }

    case EScheduleState.Cancelled: {
      return "Cancelled";
    }

    case EScheduleState.Completed: {
      return "Completed";
    }

    case EScheduleState.Unknown:
    case EScheduleState.PlateMovementError:
    default: {
      return "Unknown";
    }
  }
}

function allSchedulesCanBeMovedBack(scheduleEntries: ScheduleEntry[]) {
  const currentDate = new Date();
  for (let i = 0; i < scheduleEntries.length; i++) {
    if (DateUtil.toDate(scheduleEntries[i].dateToImage) < currentDate) {
      return false;
    }
    if (
      scheduleEntries[i].scheduleState !== EScheduleState.Queued &&
      scheduleEntries[i].scheduleState !== EScheduleState.Paused
    ) {
      return false;
    }
  }
  return true;
}

function getScheduleRowStyle(
  value: ScheduleEntry,
  isSelected: boolean
): React.CSSProperties {
  let style: React.CSSProperties = {};
  if (isSelected) {
    style.background = "#F7A246";
    style.color = "white";
    return style;
  }

  if (value.plateErrorStatus !== EPlateErrorStatus.OK) {
    style.color = "red";
    return style;
  }

  const status = value.scheduleState;

  switch (status) {
    case EScheduleState.Imaging: {
      style.background = "yellow";
      style.color = "black";
      break;
    }
    default: {
      style.color = "black";
      break;
    }
  }
  return style;
}

function getPlateAddressesByPlateIds(
  plateAddresses: PlateAddress[],
  plateIds: string[]
) {
  let result: PlateAddress[] = [];
  const filledPlateAddressed = plateAddresses.filter((p) => p.usedByPlate);
  for (let i = 0; i < plateIds.length; i++) {
    for (let j = 0; j < filledPlateAddressed.length; j++) {
      const plate = filledPlateAddressed[j].usedByPlate;
      if (plate != null) {
        if (plate.plateId === plateIds[i]) {
          result.push(filledPlateAddressed[j]);
          break;
        }
      }
    }
  }
  return result;
}

type SubMenu = "future" | "queued";

type ScheduleParams = {
  subMenu: SubMenu;
};

function Scheduling({ match }: RouteComponentProps<ScheduleParams>) {
  const body = match.params.subMenu ? match.params.subMenu : "queued";
  const modulesByType = useTypedSelector((state) => getModulesByType(state));
  const loadPort = modulesByType[EModuleType.LOADPORT][0];
  const imagers = modulesByType[EModuleType.IMAGER];
  const chambers = modulesByType[EModuleType.CHAMBER];
  const platesWaitingToBeImaged = useTypedSelector((state) =>
    getQueuedPlates(state)
  );
  const futurePlates = useTypedSelector((state) => getScheduledPlates(state));
  const plateAddresses = useTypedSelector((state) => getPlateAddresses(state));
  const [selectedImagingSchedules, setSelectedImagingSchedules] = useState(
    [] as ScheduleEntry[]
  );
  const [selectedPlateAddresses, setSelectedPlateaddresses] = useState(
    [] as PlateAddress[]
  );
  const [commandPanel, setCommandPanel] = useState(SCHEDULING);
  const [clearFilter, setClearFilter] = useState({
    action: () => {},
  });
  const suiteState = useTypedSelector(
    (state) => getModuleState(state, "Suite").suite
  );
  const [toggleSelectAllRow, setToggleSelectAllRow] = useState(
    undefined as boolean | undefined
  );

  useEffect(() => {
    const selectedPlateAddresses = getPlateAddressesByPlateIds(
      plateAddresses,
      selectedImagingSchedules.map((schedule) => schedule.plateId)
    );

    setSelectedPlateaddresses(selectedPlateAddresses);
  }, [selectedImagingSchedules]);

  const [canMovePlateByModuleIdentifier, setCanMovePlateByModuleIdentifier] =
    useState({} as { [moduleIdentifier: string]: boolean });

  useEffect(() => {
    setCanMovePlateByModuleIdentifier({});
    if (selectedPlateAddresses.length == 0) return;
    PlateApi.canMovePlateAddress(selectedPlateAddresses).then((arr) => {
      const result: { [index: string]: boolean } = arr.reduce(
        (map: { [index: string]: boolean }, obj: CanMovePlate) => (
          (map[obj.moduleIdentifier] = obj.canMove), map
        ),
        {}
      );
      setCanMovePlateByModuleIdentifier(result);
    });
  }, [selectedPlateAddresses]);

  if (suiteState == null) {
    throw new Error("Suite state is null");
  }

  const [plateDropLocationCacheStatus, setPlateDropLocationCacheStatus] =
    useState(EDropLocationCacheStatus.NoDropLocationCacheExist);

  const back = () => {
    setCommandPanel(SCHEDULING);
  };

  const onBackFromClearDropLocation = (clearDropOverride: boolean) => {
    if (clearDropOverride) {
      setPlateDropLocationCacheStatus(
        EDropLocationCacheStatus.NoDropLocationCacheExist
      );
    }
    setCommandPanel(SCHEDULING);
  };

  const close = () => {
    setSelectedImagingSchedules([]);
    setToggleSelectAllRow(false);
    setCommandPanel(SCHEDULING);
  };

  const onClick = (id: string) => {
    setCommandPanel(id);
  };

  const onCancelImaging = () => {
    SchedulingApi.doCancelSchedules(selectedImagingSchedules);
  };

  const onStartImaging = () => {
    SchedulingApi.doStartSchedules(selectedImagingSchedules);
  };

  const onStopImaging = () => {
    SchedulingApi.doStopSchedules(selectedImagingSchedules);
  };

  const onStartAllImaging = () => {
    SchedulingApi.doStartSchedules(platesWaitingToBeImaged);
  };

  const onStopAllImaging = () => {
    SchedulingApi.doStopSchedules(platesWaitingToBeImaged);
  };

  const onChangePriority = () => {
    setCommandPanel(CHANGE_PRIORITY);
  };

  const onMoveToFutureImaging = () => {
    SchedulingApi.demoteSchedule(selectedImagingSchedules);
    setSelectedImagingSchedules([]);
    setSelectedPlateaddresses([]);
  };

  const onMoveToWaitingToBeImaged = () => {
    SchedulingApi.promoteSchedule(selectedImagingSchedules);
    setSelectedImagingSchedules([]);
    setSelectedPlateaddresses([]);
  };

  let imagingSchedules = React.useMemo(
    () =>
      body === "future"
        ? futurePlates
          ? futurePlates
          : []
        : platesWaitingToBeImaged
        ? platesWaitingToBeImaged
        : [],
    [body, body === "future" ? futurePlates : platesWaitingToBeImaged]
  );

  const columns = React.useMemo(
    () => getTableColumns(body === "future"),
    [body]
  );

  useEffect(() => {
    setSelectedImagingSchedules([]);
  }, [body]);

  useEffect(() => {
    if (selectedImagingSchedules.length === 1) {
      setPlateDropLocationCacheStatus(
        EDropLocationCacheStatus.NoDropLocationCacheExist
      );
      ImagerApi.isDropLocationExist(selectedImagingSchedules[0].barcode).then(
        (value) => {
          if (selectedImagingSchedules.length === 1) {
            setPlateDropLocationCacheStatus(value);
          }
        }
      );
    }
  }, [selectedImagingSchedules]);

  if (!platesWaitingToBeImaged || !futurePlates) {
    return <div>Loading Imaging Schedule</div>;
  }

  const enableStartAllSchedules =
    suiteState.imagingMode === EImagingMode.Manual &&
    imagingSchedules.some(
      (schedule) => schedule.scheduleState === EScheduleState.Paused
    );

  const enablePauseAllSchedules =
    suiteState.imagingMode === EImagingMode.Manual &&
    imagingSchedules.some(
      (schedule) => schedule.scheduleState === EScheduleState.Queued
    );

  const enableStopSchedule =
    suiteState.imagingMode === EImagingMode.Manual &&
    selectedImagingSchedules.filter(
      (i) => i.scheduleState !== EScheduleState.Queued
    ).length === 0;

  const enableStartSchedule =
    suiteState.imagingMode === EImagingMode.Manual &&
    selectedImagingSchedules.filter(
      (i) =>
        i.scheduleState !== EScheduleState.Paused ||
        i.plateErrorStatus !== EPlateErrorStatus.OK
    ).length === 0;

  const enableImageNow =
    selectedImagingSchedules.length > 0 &&
    selectedImagingSchedules.filter(
      (i) => i.plateErrorStatus !== EPlateErrorStatus.OK
    ).length === 0;

  const enableMoveToWaitingToBeImaged =
    selectedImagingSchedules.length > 0 &&
    selectedImagingSchedules.filter(
      (i) => i.plateErrorStatus !== EPlateErrorStatus.OK
    ).length === 0;

  const enableClearDrop =
    selectedImagingSchedules.length === 1 &&
    plateDropLocationCacheStatus !==
      EDropLocationCacheStatus.NoDropLocationCacheExist;

  const targetModule = [
    loadPort.identifier,
    ...chambers.map((c) => c.identifier),
    ...imagers.map((i) => i.identifier),
  ];

  const enableMoveTo = targetModule.reduce(
    (canMove: boolean, currentValue: string) =>
      (canMove =
        canMove || (canMovePlateByModuleIdentifier[currentValue] ?? false)),
    false
  );

  const isRowSelected = !!selectedImagingSchedules.length;

  const getSelectedRowIds = () => {
    const result: number[] = [];
    selectedImagingSchedules.forEach((selected) => {
      const index = imagingSchedules.findIndex(
        (schedule) => schedule.id === selected.id
      );
      if (index !== -1) result.push(index);
    });
    return result;
  };

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[
              {
                title: body === "queued" ? "Queued" : "Future",
                to: "/schedule",
                disabled: false,
              },
            ]}
          />
          {body === "queued" && (
            <Header.RightSide>
              <HeaderButton
                id={"START_ALL"}
                label="Start All Schedules"
                icon={<IconStartAll />}
                onClick={onStartAllImaging}
                disabled={!enableStartAllSchedules}
              />
              <HeaderButton
                id={"PAUSE_ALL"}
                label="Pause All Schedules"
                icon={<IconStopAll />}
                onClick={onStopAllImaging}
                disabled={!enablePauseAllSchedules}
              />
            </Header.RightSide>
          )}
        </Header>
      </LayoutContent.Header>
      <LayoutContent.Content>
        <CommonTable<ScheduleEntry>
          setClearFilter={setClearFilter}
          data={imagingSchedules}
          columns={columns}
          initialSelectedRowIndexes={getSelectedRowIds()}
          setSelectedRows={(rows) => {
            setSelectedImagingSchedules((prev) => {
              if (JSON.stringify(prev) === JSON.stringify(rows)) return prev;
              setCommandPanel(SCHEDULING);
              return rows;
            });
          }}
          getRowStyle={getScheduleRowStyle}
          enableManualSorting={false}
          toggleSelectAllRow={toggleSelectAllRow}
          setToggleSelectAllRow={setToggleSelectAllRow}
          sortingRules={[
            {
              id: "status",
              desc: true,
            },
            {
              id: "priority",
            },
            {
              id: "dateToImage",
            },
          ]}
        />
      </LayoutContent.Content>
      <LayoutContent.SubMenu>
        <SchedulingNavSubMenu />
      </LayoutContent.SubMenu>
      <LayoutContent.CommandPanel>
        <>
          {commandPanel === SCHEDULING && (
            <CommandPanel>
              <CommandPanel.ScheduleInfoHeader
                imagingSchedules={selectedImagingSchedules}
              />
              <CommandPanel.HorizontalContent>
                {body === "queued" && (
                  <>
                    <CommandButton
                      id={START_IMAGING}
                      content="Start Schedule"
                      icon={<IconStartImaging />}
                      onClick={onStartImaging}
                      disabled={!enableStartSchedule || !isRowSelected}
                    />
                    <CommandButton
                      id={PAUSE_IMAGING}
                      content="Pause Schedule"
                      icon={<IconStopImaging />}
                      onClick={onStopImaging}
                      disabled={!enableStopSchedule || !isRowSelected}
                    />
                  </>
                )}

                <CommandButton
                  id={CANCEL_IMAGING}
                  content={
                    selectedImagingSchedules.length === 1 &&
                    selectedImagingSchedules[0].scheduleState ===
                      EScheduleState.Imaging
                      ? "Cancel Imaging"
                      : "Cancel Schedule"
                  }
                  icon={<IconCancelImaging />}
                  onClick={onCancelImaging}
                  disabled={!isRowSelected}
                />

                <CommandButton
                  id={IMAGEPLATE}
                  icon={<IconImageNow />}
                  onClick={onClick}
                  content="Image Now"
                  disabled={!enableImageNow || !isRowSelected}
                  showArrow={true}
                />

                <CommandButton
                  id={CHANGE_PRIORITY}
                  content="Set Priority"
                  icon={<IconChangePriority />}
                  onClick={onChangePriority}
                  disabled={
                    !isRowSelected ||
                    selectedImagingSchedules.length > 1 ||
                    (isRowSelected &&
                      selectedImagingSchedules[0].scheduleState ===
                        EScheduleState.Imaging) ||
                    selectedImagingSchedules[0].scheduleState ===
                      EScheduleState.Pending
                  }
                  showArrow={true}
                />

                <CommandButton
                  id={MOVE}
                  icon={<IconMovePlateTo />}
                  onClick={onClick}
                  content="Move Plate To"
                  showArrow={true}
                  disabled={!enableMoveTo || !isRowSelected}
                />

                {body === "queued" && (
                  <CommandButton
                    id={"MoveToFutureImging"}
                    icon={<IconMoveToFutureImaging />}
                    onClick={onMoveToFutureImaging}
                    content="Move Back To Future Schedule"
                    disabled={
                      !allSchedulesCanBeMovedBack(selectedImagingSchedules) ||
                      !isRowSelected
                    }
                  />
                )}

                {body === "future" && (
                  <CommandButton
                    id={"MoveToWaitingToBeImaged"}
                    icon={<IconMoveToWaitingToBeImaged />}
                    onClick={onMoveToWaitingToBeImaged}
                    content="Move To Queued Schedule"
                    disabled={!enableMoveToWaitingToBeImaged || !isRowSelected}
                  />
                )}

                <CommandButton
                  id={CLEAR_DROP}
                  icon={<IconClearDropLocation fill="#414042" />}
                  content="Clear All Drop Locations"
                  disabled={!enableClearDrop || !isRowSelected}
                  onClick={onClick}
                  showArrow={true}
                />

                <CommandButton
                  id={INFO}
                  icon={<IconPlateInfo />}
                  content="Plate Information"
                  onClick={onClick}
                  disabled={
                    !isRowSelected ||
                    (isRowSelected && selectedImagingSchedules.length > 1)
                  }
                  showArrow={true}
                />
              </CommandPanel.HorizontalContent>
            </CommandPanel>
          )}
          {commandPanel === CHANGE_PRIORITY &&
            selectedImagingSchedules.length === 1 && (
              <ChangePriority
                scheduleEntry={selectedImagingSchedules[0]}
                backBtn={back}
              />
            )}
          {commandPanel === INFO && selectedImagingSchedules.length === 1 && (
            <PlateInformation
              plateAddress={
                getPlateAddressesByPlateIds(plateAddresses, [
                  selectedImagingSchedules[0].plateId,
                ])[0]
              }
              backBtn={back}
            />
          )}
          {commandPanel === MOVE && (
            <MovePlate
              canMovePlateByModuleIdentifier={canMovePlateByModuleIdentifier}
              plateAddresses={getPlateAddressesByPlateIds(plateAddresses, [
                selectedImagingSchedules[0].plateId,
              ])}
              back={back}
              close={close}
            />
          )}
          {commandPanel === IMAGEPLATE && (
            <ImagePlateUsing
              plateAddresses={getPlateAddressesByPlateIds(
                plateAddresses,
                selectedImagingSchedules.map((schedule) => schedule.plateId)
              )}
              back={back}
              close={close}
              showImageDrop={false}
            />
          )}
          {commandPanel === CLEAR_DROP && (
            <ClearDropLocation
              plateAddress={
                getPlateAddressesByPlateIds(plateAddresses, [
                  selectedImagingSchedules[0].plateId,
                ])[0]
              }
              backBtn={onBackFromClearDropLocation}
            />
          )}
        </>
      </LayoutContent.CommandPanel>
    </LayoutContent>
  );
}

export default Scheduling;
